import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/Icon.js';
import {
  Loader2Icon,
  PlusIcon,
  PencilIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
  CheckIcon,
} from '../components/icons.js';

/**
 * Specialisations admin page — manages the taxonomy used to tag
 * Documents, Confluence spaces and Code source repos.
 *
 * Layout (matches design canvas in `knowledge-base/project/redesign/
 * page-specialisations.jsx`):
 *
 *   ┌── KPI strip ────────────────────────────────────────────────┐
 *   │  Specialisations · Total assignments · Orphans · Last edit  │
 *   ├── Master list (360) ────────────┬── Detail pane ────────────┤
 *   │  search box                     │  spec header + edit       │
 *   │  spec row · spec row · …        │  tabs (docs / conf / code)│
 *   │  + New spec                     │  toolbar (search + assign)│
 *   │                                 │  member rows · …          │
 *   └─────────────────────────────────┴───────────────────────────┘
 *
 * Replaces the hardcoded-string-array workflow in `shared/src/sociale-
 * kaart.ts` — admins can now create, rename, merge and delete
 * specialisations from the UI. Assignment to documents stays available
 * via the inline editor on /admin/documents; the new "Assign items"
 * modal on this page lets admins bulk-tag confluence + code sources
 * (which don't have an inline editor anywhere else).
 */

const ERROR_FALLBACK = 'Something went wrong. Please try again.';

function errorMessage(err: unknown, fallback = ERROR_FALLBACK): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

interface Specialisation {
  id: string;
  name: string;
  description: string;
  colour: string;
  ring: 'ring0' | 'ring1';
  owner: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

interface SpecStats {
  name: string;
  documents: number;
  confluence: number;
  code: number;
  total: number;
}

interface Summary {
  specialisationsCount: number;
  totalAssignments: number;
  orphanCount: number;
  lastEdited: string | null;
}

interface Member {
  id: string;
  title: string;
  subtitle?: string;
  ring?: 'ring0' | 'ring1';
  classification?: string;
}

interface AssignableItem {
  id: string;
  title: string;
  subtitle?: string;
  existingSpecs: string[];
}

type Kind = 'documents' | 'confluence' | 'code';

const SWATCHES: string[] = [
  '#172E57',
  '#3B5AA8',
  '#2F80ED',
  '#018236',
  '#BA0517',
  '#E97109',
  '#828282',
  '#8A3F00',
];

function kindIcon(kind: Kind): string {
  return kind === 'documents'
    ? 'folder_open'
    : kind === 'confluence'
      ? 'hub'
      : 'integration_instructions';
}

function kindLabel(kind: Kind): string {
  return kind === 'documents'
    ? 'Documents'
    : kind === 'confluence'
      ? 'Confluence spaces'
      : 'Code sources';
}

function ringLabel(ring: 'ring0' | 'ring1'): string {
  return ring === 'ring0' ? 'Ring 0 · Internal' : 'Ring 1 · Product';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function AdminSpecialisationsPage(): JSX.Element {
  const [specs, setSpecs] = useState<Specialisation[]>([]);
  const [stats, setStats] = useState<SpecStats[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<Kind>('documents');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '', colour: '', owner: '' });
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const [members, setMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, summaryRes] = await Promise.all([
        apiGet<{ specs: Specialisation[]; stats: SpecStats[] }>('/api/v1/admin/specialisations'),
        apiGet<Summary>('/api/v1/admin/specialisations/summary'),
      ]);
      setSpecs(listRes.specs);
      setStats(listRes.stats);
      setSummary(summaryRes);
      // Preserve current selection if it still exists; otherwise default to first.
      setSelectedId((cur) => {
        if (cur && listRes.specs.some((s) => s.id === cur)) return cur;
        return listRes.specs[0]?.id ?? null;
      });
    } catch (err) {
      setError(errorMessage(err, 'Could not load specialisations.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => specs.find((s) => s.id === selectedId) ?? null,
    [specs, selectedId],
  );
  const selectedStats = useMemo(
    () => (selected ? stats.find((s) => s.name === selected.name) : null) ?? null,
    [stats, selected],
  );

  const loadMembers = useCallback(async (): Promise<void> => {
    if (!selected) {
      setMembers([]);
      return;
    }
    setMembersLoading(true);
    try {
      const res = await apiGet<{ members: Member[] }>(
        `/api/v1/admin/specialisations/${selected.id}/members?kind=${tab}`,
      );
      setMembers(res.members);
    } catch (err) {
      setError(errorMessage(err, 'Could not load tagged items.'));
    } finally {
      setMembersLoading(false);
    }
  }, [selected, tab]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  // Reset edit-draft when the selection changes.
  useEffect(() => {
    if (selected && editing) {
      setDraft({
        name: selected.name,
        description: selected.description,
        colour: selected.colour,
        owner: selected.owner,
      });
    }
  }, [editing, selected]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = needle
      ? specs.filter(
          (s) =>
            s.name.toLowerCase().includes(needle) ||
            s.description.toLowerCase().includes(needle) ||
            s.owner.toLowerCase().includes(needle),
        )
      : specs;
    return [...matches].sort((a, b) => {
      const at = stats.find((s) => s.name === a.name)?.total ?? 0;
      const bt = stats.find((s) => s.name === b.name)?.total ?? 0;
      if (bt !== at) return bt - at;
      return a.name.localeCompare(b.name);
    });
  }, [specs, stats, q]);

  const onUnassign = async (memberId: string): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      await apiPost(`/api/v1/admin/specialisations/${selected.id}/unassign`, {
        kind: tab,
        ids: [memberId],
      });
      await Promise.all([loadMembers(), load()]);
    } catch (err) {
      setError(errorMessage(err, 'Could not unassign item.'));
    } finally {
      setBusy(false);
    }
  };

  const onSaveEdit = async (): Promise<void> => {
    if (!selected) return;
    setSaving(true);
    try {
      const patch: Partial<Specialisation> = {};
      if (draft.name.trim() !== selected.name) patch.name = draft.name.trim();
      if (draft.description.trim() !== selected.description)
        patch.description = draft.description.trim();
      if (draft.colour !== selected.colour) patch.colour = draft.colour;
      if (draft.owner.trim() !== selected.owner) patch.owner = draft.owner.trim();
      if (Object.keys(patch).length === 0) {
        setEditing(false);
        return;
      }
      await apiPatch(`/api/v1/admin/specialisations/${selected.id}`, patch);
      setEditing(false);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not save changes.'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (force: boolean): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      await apiDelete(`/api/v1/admin/specialisations/${selected.id}${force ? '?force=true' : ''}`);
      setConfirmDelete(false);
      setSelectedId(null);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete specialisation.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="flex h-full min-h-0 flex-col bg-nebula-app">
        <div className="px-6 pt-6">
          <SummaryStrip summary={summary} loading={loading} />
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-md border border-nebula-error/30 bg-nebula-error-bg px-4 py-2.5 text-sm text-nebula-error">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 text-nebula-error/70 hover:text-nebula-error"
              aria-label="Dismiss error"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}

        <div className="grid min-h-0 flex-1 grid-cols-[360px_1fr] gap-4 overflow-hidden px-6 pb-6 pt-4">
          {/* Master list */}
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-nebula-border-soft bg-nebula-surface">
            <div className="flex items-center gap-2 border-b border-nebula-border-soft p-3">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-nebula-border-soft bg-nebula-app px-2.5 py-1.5">
                <SearchIcon size={14} className="text-nebula-fg-4" />
                <input
                  type="text"
                  value={q}
                  onChange={(e): void => setQ(e.target.value)}
                  placeholder="Find a specialisation…"
                  className="flex-1 bg-transparent text-sm text-nebula-fg-1 outline-none placeholder:text-nebula-fg-4"
                />
              </div>
            </div>

            <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-2">
              {loading && specs.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-nebula-fg-3">
                  <Loader2Icon size={14} className="mr-2 animate-spin" />
                  Loading…
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-10 text-center text-xs text-nebula-fg-3">
                  {specs.length === 0
                    ? 'No specialisations yet. Click + to create your first one.'
                    : 'No matches.'}
                </div>
              ) : (
                filtered.map((s) => {
                  const on = s.id === selectedId;
                  const total = stats.find((row) => row.name === s.name)?.total ?? 0;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(): void => {
                        setSelectedId(s.id);
                        setEditing(false);
                      }}
                      className={[
                        'mb-1 flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                        on ? 'bg-nebula-tint' : 'hover:bg-nebula-gray-5',
                      ].join(' ')}
                      style={
                        on ? { borderLeft: `3px solid ${s.colour}`, paddingLeft: 7 } : undefined
                      }
                    >
                      <span
                        className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-white"
                        style={{ background: s.colour }}
                      >
                        <Icon name="sell" size={12} fill />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`truncate text-[13px] font-ui ${
                              on
                                ? 'font-semibold text-nebula-primary'
                                : 'font-semibold text-nebula-fg-1'
                            }`}
                          >
                            {s.name}
                          </span>
                          <span
                            className={`ml-auto rounded-full px-1.5 py-px text-[10px] font-numeric font-semibold ${
                              on
                                ? 'bg-nebula-surface text-nebula-secondary'
                                : 'bg-nebula-gray-5 text-nebula-fg-3'
                            }`}
                          >
                            {total}
                          </span>
                        </div>
                        {s.description && (
                          <div className="mt-0.5 truncate text-[11px] text-nebula-fg-3">
                            {s.description}
                          </div>
                        )}
                        <div className="mt-1 inline-flex gap-1 text-[10px] text-nebula-fg-3">
                          <span
                            className={`rounded-full border border-nebula-border-soft bg-nebula-surface px-1.5 py-px font-numeric font-semibold ${
                              s.ring === 'ring0' ? 'text-nebula-fg-2' : 'text-nebula-secondary'
                            }`}
                          >
                            {s.ring === 'ring0' ? 'R0' : 'R1'}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className="border-t border-nebula-border-soft p-2.5">
              <button
                type="button"
                onClick={(): void => setShowCreate(true)}
                className="flex w-full items-center justify-center gap-1.5 rounded-md bg-nebula-primary px-3 py-2 text-sm font-medium text-white hover:bg-nebula-primary/90"
              >
                <PlusIcon size={14} />
                New specialisation
              </button>
            </div>
          </section>

          {/* Detail pane */}
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-nebula-border-soft bg-nebula-surface">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center p-10 text-center">
                <div>
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-nebula-tint">
                    <Icon name="sell" size={22} className="text-nebula-secondary" />
                  </div>
                  <div className="text-sm font-medium text-nebula-fg-1">Pick a specialisation</div>
                  <div className="mt-1 max-w-md text-xs text-nebula-fg-3">
                    Select a specialisation on the left to see every Document, Confluence space and
                    Code source tagged with it.
                  </div>
                </div>
              </div>
            ) : (
              <DetailPane
                selected={selected}
                selectedStats={selectedStats}
                editing={editing}
                draft={draft}
                setDraft={setDraft}
                onStartEdit={(): void => {
                  setDraft({
                    name: selected.name,
                    description: selected.description,
                    colour: selected.colour,
                    owner: selected.owner,
                  });
                  setEditing(true);
                }}
                onCancelEdit={(): void => setEditing(false)}
                onSaveEdit={onSaveEdit}
                saving={saving}
                onMerge={(): void => setShowMerge(true)}
                onDelete={(): void => setConfirmDelete(true)}
                tab={tab}
                setTab={setTab}
                members={members}
                membersLoading={membersLoading}
                onAssignClick={(): void => setShowAssign(true)}
                onUnassign={onUnassign}
                busy={busy}
              />
            )}
          </section>
        </div>
      </div>

      {showCreate && (
        <CreateSpecialisationModal
          onClose={(): void => setShowCreate(false)}
          onCreated={async (id): Promise<void> => {
            setShowCreate(false);
            await load();
            setSelectedId(id);
          }}
        />
      )}

      {showAssign && selected && (
        <AssignItemsModal
          specId={selected.id}
          specName={selected.name}
          specColour={selected.colour}
          kind={tab}
          onClose={(): void => setShowAssign(false)}
          onAssigned={async (): Promise<void> => {
            setShowAssign(false);
            await Promise.all([loadMembers(), load()]);
          }}
        />
      )}

      {showMerge && selected && (
        <MergeModal
          spec={selected}
          allSpecs={specs}
          stats={stats}
          onClose={(): void => setShowMerge(false)}
          onMerged={async (): Promise<void> => {
            setShowMerge(false);
            setSelectedId(null);
            await load();
          }}
        />
      )}

      {confirmDelete && selected && (
        <ConfirmDeleteModal
          spec={selected}
          assignments={selectedStats?.total ?? 0}
          onCancel={(): void => setConfirmDelete(false)}
          onConfirm={onDelete}
          busy={busy}
        />
      )}
    </Layout>
  );
}

function SummaryStrip({
  summary,
  loading,
}: {
  summary: Summary | null;
  loading: boolean;
}): JSX.Element {
  const cards = [
    {
      label: 'Specialisations',
      value: summary?.specialisationsCount,
      icon: 'sell',
      accent: true,
    },
    {
      label: 'Total assignments',
      value: summary?.totalAssignments,
      icon: 'link',
      accent: false,
    },
    {
      label: 'Items without tags',
      value: summary?.orphanCount,
      icon: 'report',
      tone: 'warning' as const,
    },
    {
      label: 'Last edited',
      value: summary?.lastEdited ? formatDate(summary.lastEdited) : '—',
      icon: 'schedule',
      mono: true,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl border border-nebula-border-soft bg-nebula-surface p-4"
        >
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs text-nebula-fg-3">{c.label}</span>
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                'tone' in c && c.tone === 'warning'
                  ? 'bg-nebula-warning-bg text-nebula-warning'
                  : c.accent
                    ? 'bg-nebula-primary text-white'
                    : 'bg-nebula-tint text-nebula-secondary'
              }`}
            >
              <Icon name={c.icon} size={15} />
            </span>
          </div>
          <div
            className={`font-numeric font-bold tracking-tight text-nebula-fg-1 ${
              c.mono ? 'text-lg' : 'text-2xl'
            }`}
          >
            {loading && c.value === undefined ? '—' : (c.value ?? '—')}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailPane(props: {
  selected: Specialisation;
  selectedStats: SpecStats | null;
  editing: boolean;
  draft: { name: string; description: string; colour: string; owner: string };
  setDraft: React.Dispatch<
    React.SetStateAction<{ name: string; description: string; colour: string; owner: string }>
  >;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  saving: boolean;
  onMerge: () => void;
  onDelete: () => void;
  tab: Kind;
  setTab: (k: Kind) => void;
  members: Member[];
  membersLoading: boolean;
  onAssignClick: () => void;
  onUnassign: (id: string) => void;
  busy: boolean;
}): JSX.Element {
  const {
    selected,
    selectedStats,
    editing,
    draft,
    setDraft,
    onStartEdit,
    onCancelEdit,
    onSaveEdit,
    saving,
    onMerge,
    onDelete,
    tab,
    setTab,
    members,
    membersLoading,
    onAssignClick,
    onUnassign,
    busy,
  } = props;

  const tabs: Array<{ key: Kind; label: string; count: number }> = [
    {
      key: 'documents',
      label: 'Documents',
      count: selectedStats?.documents ?? 0,
    },
    {
      key: 'confluence',
      label: 'Confluence spaces',
      count: selectedStats?.confluence ?? 0,
    },
    { key: 'code', label: 'Code sources', count: selectedStats?.code ?? 0 },
  ];

  return (
    <>
      {/* Header */}
      <div
        className="border-b border-nebula-border-soft p-5"
        style={{
          background: `linear-gradient(180deg, ${hexToTint(selected.colour, 0.1)} 0%, #fff 100%)`,
        }}
      >
        <div className="flex items-start gap-4">
          <span
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-white"
            style={{ background: selected.colour }}
          >
            <Icon name="sell" size={22} fill />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              {editing ? (
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e): void => setDraft((d) => ({ ...d, name: e.target.value }))}
                  className="font-display text-xl font-semibold text-nebula-fg-1 border-b border-dashed border-nebula-border bg-transparent outline-none"
                  autoFocus
                />
              ) : (
                <h2 className="font-display text-xl font-bold text-nebula-fg-1">{selected.name}</h2>
              )}
              <span className="font-numeric text-xs text-nebula-fg-3">
                · {selectedStats?.total ?? 0} assignments
              </span>
              <span className="ml-auto inline-flex gap-1.5">
                {!editing ? (
                  <>
                    <button
                      type="button"
                      onClick={onStartEdit}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint"
                    >
                      <PencilIcon size={13} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={onMerge}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint"
                    >
                      <Icon name="cell_merge" size={13} />
                      Merge
                    </button>
                    <button
                      type="button"
                      onClick={onDelete}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-nebula-error hover:bg-nebula-error-bg"
                    >
                      <Trash2Icon size={13} />
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={onCancelEdit}
                      disabled={saving}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-nebula-fg-3 hover:bg-nebula-gray-5"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onSaveEdit}
                      disabled={saving}
                      className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-2 py-1 text-xs font-medium text-white hover:bg-nebula-primary/90 disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader2Icon size={13} className="animate-spin" />
                      ) : (
                        <CheckIcon size={13} />
                      )}
                      Save
                    </button>
                  </>
                )}
              </span>
            </div>

            {editing ? (
              <textarea
                value={draft.description}
                onChange={(e): void => setDraft((d) => ({ ...d, description: e.target.value }))}
                rows={2}
                placeholder="One-sentence description of when this specialisation applies."
                className="mt-2 w-full rounded-md border border-dashed border-nebula-border bg-white p-2 text-xs text-nebula-fg-2 outline-none focus:border-nebula-secondary"
              />
            ) : (
              selected.description && (
                <p className="mt-1 max-w-2xl text-xs text-nebula-fg-2">{selected.description}</p>
              )
            )}

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-nebula-fg-3">
              <span className="inline-flex items-center gap-1 rounded-full border border-nebula-border-soft bg-white px-2 py-0.5">
                <Icon name="person" size={12} />
                Owner:{' '}
                {editing ? (
                  <input
                    type="text"
                    value={draft.owner}
                    onChange={(e): void => setDraft((d) => ({ ...d, owner: e.target.value }))}
                    placeholder="HR / Engineering / …"
                    className="w-32 bg-transparent text-[11px] outline-none"
                  />
                ) : (
                  selected.owner || '—'
                )}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Icon name="schedule" size={12} />
                Updated {formatDate(selected.updatedAt)}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5">
                Colour
                <ColourPicker
                  value={editing ? draft.colour : selected.colour}
                  editable={editing}
                  onChange={(c): void => setDraft((d) => ({ ...d, colour: c }))}
                />
              </span>
              <span>·</span>
              <span className="font-mono">{ringLabel(selected.ring)}</span>
              <span>·</span>
              <span className="font-mono">id: {selected.id}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-nebula-border-soft px-5">
        <nav className="-mb-px flex items-center gap-5">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={(): void => setTab(t.key)}
              className={[
                'flex items-center gap-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-colors',
                t.key === tab
                  ? 'border-nebula-primary text-nebula-primary'
                  : 'border-transparent text-nebula-fg-3 hover:text-nebula-fg-1',
              ].join(' ')}
            >
              <Icon name={kindIcon(t.key)} size={15} />
              {t.label}
              <span
                className={`rounded-full px-1.5 text-[10px] font-numeric font-semibold ${
                  t.key === tab
                    ? 'bg-nebula-tint text-nebula-secondary'
                    : 'bg-nebula-gray-5 text-nebula-fg-3'
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-nebula-border-soft bg-nebula-gray-6 px-5 py-2.5">
        <div className="flex-1 text-xs text-nebula-fg-3">
          {membersLoading
            ? 'Loading…'
            : members.length === 0
              ? `Nothing tagged with this ${tab === 'documents' ? 'specialisation in any document' : tab === 'confluence' ? 'specialisation in any Confluence space' : 'specialisation in any code source'}.`
              : `${members.length} ${kindLabel(tab).toLowerCase()} tagged`}
        </div>
        <button
          type="button"
          onClick={onAssignClick}
          className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-2.5 py-1.5 text-xs font-medium text-white hover:bg-nebula-primary/90"
        >
          <PlusIcon size={12} />
          Assign {tab === 'documents' ? 'documents' : tab === 'confluence' ? 'spaces' : 'repos'}
        </button>
      </div>

      {/* Member list */}
      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto">
        {membersLoading && members.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-nebula-fg-3">
            <Loader2Icon size={14} className="mr-2 animate-spin" />
            Loading…
          </div>
        ) : members.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-nebula-gray-5 text-nebula-fg-3">
              <Icon name="filter_none" size={18} />
            </div>
            <div className="text-sm font-medium text-nebula-fg-1">
              No {kindLabel(tab).toLowerCase()} tagged yet
            </div>
            <div className="mt-1 text-xs text-nebula-fg-3">
              Use “Assign” above to apply this specialisation to existing items.
            </div>
          </div>
        ) : (
          members.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 border-b border-nebula-border-soft px-5 py-2.5 hover:bg-nebula-gray-6"
            >
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
                  tab === 'documents'
                    ? 'bg-nebula-error-bg text-nebula-error'
                    : tab === 'confluence'
                      ? 'bg-nebula-tint text-nebula-secondary'
                      : 'bg-nebula-gray-5 text-nebula-fg-2'
                }`}
              >
                <Icon
                  name={tab === 'documents' ? 'description' : tab === 'confluence' ? 'hub' : 'code'}
                  size={16}
                />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-nebula-fg-1 font-ui">
                  {m.title}
                </div>
                {m.subtitle && (
                  <div className="truncate text-[11px] text-nebula-fg-3">{m.subtitle}</div>
                )}
              </div>
              <button
                type="button"
                onClick={(): void => onUnassign(m.id)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-nebula-fg-3 hover:bg-nebula-error-bg hover:text-nebula-error disabled:opacity-50"
              >
                <XIcon size={12} />
                Unassign
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function ColourPicker({
  value,
  editable,
  onChange,
}: {
  value: string;
  editable: boolean;
  onChange: (c: string) => void;
}): JSX.Element {
  if (!editable) {
    return (
      <span
        className="inline-block h-3.5 w-3.5 rounded"
        style={{ background: value }}
        aria-label={`Colour ${value}`}
      />
    );
  }
  return (
    <span className="inline-flex gap-1">
      {SWATCHES.map((c) => (
        <button
          key={c}
          type="button"
          onClick={(): void => onChange(c)}
          title={c}
          className="h-4 w-4 rounded"
          style={{
            background: c,
            border: c === value ? '2px solid #0E0C14' : '2px solid transparent',
            boxShadow: c === value ? 'inset 0 0 0 1px #fff' : 'none',
          }}
        />
      ))}
    </span>
  );
}

function CreateSpecialisationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [colour, setColour] = useState(SWATCHES[0]!);
  const [ring, setRing] = useState<'ring0' | 'ring1'>('ring1');
  const [owner, setOwner] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await apiPost<{ spec: Specialisation }>('/api/v1/admin/specialisations', {
        name: name.trim(),
        description: description.trim(),
        colour,
        ring,
        owner: owner.trim(),
      });
      await onCreated(res.spec.id);
    } catch (e2) {
      setErr(errorMessage(e2, 'Could not create.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="New specialisation" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4 p-5">
        <Field label="Name" required>
          <input
            value={name}
            onChange={(e): void => setName(e.target.value)}
            placeholder="e.g. Cloud Migration"
            className="w-full rounded-md border border-nebula-border-soft px-3 py-2 text-sm outline-none focus:border-nebula-secondary"
            maxLength={60}
            autoFocus
          />
        </Field>
        <Field label="Description (optional)">
          <textarea
            value={description}
            onChange={(e): void => setDescription(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="One sentence on when this applies."
            className="w-full rounded-md border border-nebula-border-soft px-3 py-2 text-sm outline-none focus:border-nebula-secondary"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Ring" required>
            <select
              value={ring}
              onChange={(e): void => setRing(e.target.value as 'ring0' | 'ring1')}
              className="w-full rounded-md border border-nebula-border-soft px-3 py-2 text-sm outline-none focus:border-nebula-secondary"
            >
              <option value="ring1">Ring 1 · Product</option>
              <option value="ring0">Ring 0 · Internal</option>
            </select>
          </Field>
          <Field label="Owner (optional)">
            <input
              value={owner}
              onChange={(e): void => setOwner(e.target.value)}
              maxLength={120}
              placeholder="HR / Engineering / Andre"
              className="w-full rounded-md border border-nebula-border-soft px-3 py-2 text-sm outline-none focus:border-nebula-secondary"
            />
          </Field>
        </div>
        <Field label="Colour">
          <ColourPicker value={colour} editable onChange={setColour} />
        </Field>
        {err && (
          <div className="rounded-md border border-nebula-error/30 bg-nebula-error-bg px-3 py-2 text-xs text-nebula-error">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-nebula-border-soft pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-nebula-fg-3 hover:bg-nebula-gray-5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-primary/90 disabled:opacity-50"
          >
            {saving && <Loader2Icon size={13} className="animate-spin" />}
            Create
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function AssignItemsModal({
  specId,
  specName,
  specColour,
  kind,
  onClose,
  onAssigned,
}: {
  specId: string;
  specName: string;
  specColour: string;
  kind: Kind;
  onClose: () => void;
  onAssigned: () => void | Promise<void>;
}): JSX.Element {
  const [items, setItems] = useState<AssignableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiGet<{ items: AssignableItem[] }>(
      `/api/v1/admin/specialisations/${specId}/assignable?kind=${kind}`,
    )
      .then((res) => {
        if (alive) setItems(res.items);
      })
      .catch((e) => {
        if (alive) setErr(errorMessage(e, 'Could not load items.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [specId, kind]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (i) =>
        i.title.toLowerCase().includes(needle) || (i.subtitle ?? '').toLowerCase().includes(needle),
    );
  }, [items, q]);

  const toggle = (id: string): void => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = async (): Promise<void> => {
    if (picked.size === 0) return;
    setSaving(true);
    try {
      await apiPost(`/api/v1/admin/specialisations/${specId}/assign`, {
        kind,
        ids: Array.from(picked),
      });
      await onAssigned();
    } catch (e) {
      setErr(errorMessage(e, 'Could not assign.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      width="720px"
      title={
        <>
          Assign {kindLabel(kind).toLowerCase()} to{' '}
          <span style={{ color: specColour }}>{specName}</span>
        </>
      }
      onClose={onClose}
    >
      <div className="border-b border-nebula-border-soft p-4">
        <div className="flex items-center gap-2 rounded-md border border-nebula-border-soft bg-nebula-app px-2.5 py-1.5">
          <SearchIcon size={13} className="text-nebula-fg-4" />
          <input
            type="text"
            value={q}
            onChange={(e): void => setQ(e.target.value)}
            placeholder={`Search ${kindLabel(kind).toLowerCase()}…`}
            className="flex-1 bg-transparent text-sm text-nebula-fg-1 outline-none placeholder:text-nebula-fg-4"
          />
        </div>
      </div>
      <div className="thin-scroll max-h-[55vh] min-h-[200px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-nebula-fg-3">
            <Loader2Icon size={14} className="mr-2 animate-spin" />
            Loading…
          </div>
        ) : err ? (
          <div className="p-4 text-sm text-nebula-error">{err}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-nebula-fg-3">
            {items.length === 0
              ? `Every ${kindLabel(kind).slice(0, -1).toLowerCase()} already carries this specialisation.`
              : 'No matches.'}
          </div>
        ) : (
          filtered.map((it) => {
            const isPicked = picked.has(it.id);
            return (
              <label
                key={it.id}
                className={`flex cursor-pointer items-center gap-3 border-b border-nebula-border-soft px-4 py-2.5 ${
                  isPicked ? 'bg-nebula-tint' : 'hover:bg-nebula-gray-6'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isPicked}
                  onChange={(): void => toggle(it.id)}
                  className="accent-nebula-primary"
                />
                <span
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${
                    kind === 'documents'
                      ? 'bg-nebula-error-bg text-nebula-error'
                      : kind === 'confluence'
                        ? 'bg-nebula-tint text-nebula-secondary'
                        : 'bg-nebula-gray-5 text-nebula-fg-2'
                  }`}
                >
                  <Icon
                    name={
                      kind === 'documents' ? 'description' : kind === 'confluence' ? 'hub' : 'code'
                    }
                    size={14}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-nebula-fg-1 font-ui">
                    {it.title}
                  </div>
                  {it.subtitle && (
                    <div className="truncate text-[11px] text-nebula-fg-3">{it.subtitle}</div>
                  )}
                </div>
                {it.existingSpecs.length > 0 && (
                  <div className="flex flex-shrink-0 gap-1">
                    {it.existingSpecs.slice(0, 2).map((s) => (
                      <span
                        key={s}
                        className="rounded-full bg-nebula-gray-5 px-1.5 py-px text-[10px] text-nebula-fg-2"
                      >
                        {s}
                      </span>
                    ))}
                    {it.existingSpecs.length > 2 && (
                      <span className="rounded-full bg-nebula-gray-5 px-1.5 py-px text-[10px] text-nebula-fg-2">
                        +{it.existingSpecs.length - 2}
                      </span>
                    )}
                  </div>
                )}
              </label>
            );
          })
        )}
      </div>
      <div className="flex items-center gap-3 border-t border-nebula-border-soft p-4">
        <span className="text-xs text-nebula-fg-3">
          <strong className="font-numeric text-nebula-fg-1">{picked.size}</strong> selected
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-nebula-fg-3 hover:bg-nebula-gray-5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={picked.size === 0 || saving}
          className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-primary/90 disabled:opacity-50"
        >
          {saving && <Loader2Icon size={13} className="animate-spin" />}
          Assign{picked.size > 0 ? ` (${picked.size})` : ''}
        </button>
      </div>
    </ModalShell>
  );
}

function MergeModal({
  spec,
  allSpecs,
  stats,
  onClose,
  onMerged,
}: {
  spec: Specialisation;
  allSpecs: Specialisation[];
  stats: SpecStats[];
  onClose: () => void;
  onMerged: () => void | Promise<void>;
}): JSX.Element {
  const [intoId, setIntoId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Can only merge within the same ring (service throws otherwise).
  const candidates = allSpecs.filter((s) => s.id !== spec.id && s.ring === spec.ring);
  const currentStats = stats.find((s) => s.name === spec.name);

  const onSubmit = async (): Promise<void> => {
    if (!intoId) return;
    setSaving(true);
    setErr(null);
    try {
      await apiPost(`/api/v1/admin/specialisations/${spec.id}/merge`, { intoId });
      await onMerged();
    } catch (e) {
      setErr(errorMessage(e, 'Could not merge.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Merge specialisation" onClose={onClose}>
      <div className="p-5 text-sm text-nebula-fg-2">
        <p>
          Every item tagged with <strong style={{ color: spec.colour }}>{spec.name}</strong> (
          {currentStats?.total ?? 0} items) will be retagged to the target below, and{' '}
          <strong>{spec.name}</strong> will be deleted.
        </p>
        <p className="mt-2 text-xs text-nebula-fg-3">
          You can only merge within the same ring ({ringLabel(spec.ring)}).
        </p>
        <div className="mt-4">
          <Field label="Merge into" required>
            <select
              value={intoId}
              onChange={(e): void => setIntoId(e.target.value)}
              className="w-full rounded-md border border-nebula-border-soft px-3 py-2 text-sm outline-none focus:border-nebula-secondary"
            >
              <option value="">Select a target…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {err && (
          <div className="mt-3 rounded-md border border-nebula-error/30 bg-nebula-error-bg px-3 py-2 text-xs text-nebula-error">
            {err}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-nebula-border-soft p-4">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-nebula-fg-3 hover:bg-nebula-gray-5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!intoId || saving}
          className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-primary/90 disabled:opacity-50"
        >
          {saving && <Loader2Icon size={13} className="animate-spin" />}
          Merge
        </button>
      </div>
    </ModalShell>
  );
}

function ConfirmDeleteModal({
  spec,
  assignments,
  onCancel,
  onConfirm,
  busy,
}: {
  spec: Specialisation;
  assignments: number;
  onCancel: () => void;
  onConfirm: (force: boolean) => void;
  busy: boolean;
}): JSX.Element {
  const hasAssignments = assignments > 0;
  return (
    <ModalShell title="Delete specialisation" onClose={onCancel}>
      <div className="p-5 text-sm text-nebula-fg-2">
        <p>
          Delete <strong style={{ color: spec.colour }}>{spec.name}</strong>?
        </p>
        {hasAssignments ? (
          <p className="mt-2 rounded-md border border-nebula-warning/30 bg-nebula-warning-bg px-3 py-2 text-xs text-nebula-warning">
            This specialisation is currently applied to <strong>{assignments}</strong> item(s).
            Deleting will strip the tag from every one of them. Consider <em>Merge</em> if you want
            to preserve their topical grouping.
          </p>
        ) : (
          <p className="mt-2 text-xs text-nebula-fg-3">Nothing is currently tagged with it.</p>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-nebula-border-soft p-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-nebula-fg-3 hover:bg-nebula-gray-5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={(): void => onConfirm(hasAssignments)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-nebula-error px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-error/90 disabled:opacity-50"
        >
          {busy && <Loader2Icon size={13} className="animate-spin" />}
          {hasAssignments ? `Delete + strip ${assignments} tag(s)` : 'Delete'}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  width = '520px',
  onClose,
  children,
}: {
  title: React.ReactNode;
  width?: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40"
    >
      <div
        onClick={(e): void => e.stopPropagation()}
        className="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-xl bg-nebula-surface shadow-card"
        style={{ maxWidth: width, boxShadow: 'var(--ov-shadow-popover)' }}
      >
        <div className="flex items-center justify-between border-b border-nebula-border-soft px-5 py-3">
          <div className="font-ui text-sm font-semibold text-nebula-fg-1">{title}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-gray-5 hover:text-nebula-fg-1"
          >
            <XIcon size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-nebula-fg-3">
        {label}
        {required && <span className="text-nebula-error">*</span>}
      </span>
      {children}
    </label>
  );
}

function hexToTint(hex: string, alpha = 0.1): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
